/**
 * ScribbleCard — a reading-journal entry, not a social-media post. Small
 * cover, star rating, a pulled quote set in the display face, then the
 * reader's own reflection. Quiet like/reply affordance at the very bottom.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Heart, MessageCircle } from 'lucide-react';
import { Stars, cx } from '@/components/site/primitives';
import { fadeUp } from '@/lib/motion';
import { timeAgo } from './utils/profileHelpers';
import BookCover from '@/components/books/BookCoverArt';
import { PROFILE } from '@/constants/testIds';

export default function ScribbleCard({ scribble, authorName, onToggleLike }) {
  return (
    <motion.article
      variants={fadeUp}
      data-testid={PROFILE.scribbleCard}
      className="flex gap-5 border-b border-line py-8 first:pt-0 last:border-b-0"
    >
      <BookCover
        book={{ id: scribble.bookId, title: scribble.bookTitle, author: scribble.bookAuthor, tone: scribble.tone }}
        size="xs"
        className="w-16 shrink-0 sm:w-20"
      />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <h3 className="font-display text-lg font-semibold text-ink">{scribble.bookTitle}</h3>
          <span className="text-sm text-subtle">by {scribble.bookAuthor}</span>
        </div>
        <div className="mt-1"><Stars value={scribble.rating} size={13} /></div>

        <blockquote className="mt-4 font-display text-xl leading-snug text-ink text-balance">
          &ldquo;{scribble.quote}&rdquo;
        </blockquote>

        {scribble.body && <p className="mt-3 text-sm leading-relaxed text-subtle">{scribble.body}</p>}

        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-subtle">
            <span className="font-medium text-ink/80">{authorName}&rsquo;s Scribble</span> · {timeAgo(scribble.createdAt)}
          </p>
          <div className="flex items-center gap-4 text-xs font-medium text-subtle">
            <button
              type="button"
              onClick={() => onToggleLike?.(scribble.id)}
              className="inline-flex items-center gap-1.5 transition-colors hover:text-coral"
              aria-pressed={!!scribble.liked}
              aria-label={scribble.liked ? 'Unlike this scribble' : 'Like this scribble'}
            >
              <Heart className={cx('h-3.5 w-3.5', scribble.liked && 'fill-coral text-coral')} /> {scribble.likes}
            </button>
            <span className="inline-flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5" /> Reply</span>
          </div>
        </div>
      </div>
    </motion.article>
  );
}
